import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/services/api"
import { addDays, endOfWeek, startOfWeek } from "date-fns"
import type { Activity } from "../entities/activities"

export function useGetActivitiesMetrics() {
  const [currentApprovedActivities, setCurrentApprovedActivities] = useState<Activity[]>([])
  const [previousApprovedActivities, setPreviousApprovedActivities] = useState<Activity[]>([])

  const now = new Date()
  const currentStartDate = startOfWeek(now, { weekStartsOn: 1 })
  const currentEndDate = endOfWeek(addDays(currentStartDate, 7), { weekStartsOn: 1 })

  const previousStartDate = startOfWeek(addDays(now, -7), { weekStartsOn: 1 })
  const previousEndDate = endOfWeek(addDays(previousStartDate, 7), { weekStartsOn: 1 })

  const currentActivitiesQuery = useQuery({
    queryKey: ["get-current-approved-activities"],
    queryFn: async () =>
      await api.cronos.activities.get({
        startDate: currentStartDate.toISOString(),
        endDate: currentEndDate.toISOString(),
      }),
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (data) => data.data.description.filter((activity) => activity.status === "approved"),
  })

  const previousActivitiesQuery = useQuery({
    queryKey: ["get-previous-approved-activities"],
    queryFn: async () =>
      await api.cronos.activities.get({
        startDate: previousStartDate.toISOString(),
        endDate: previousEndDate.toISOString(),
      }),
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (data) => data.data.description.filter((activity) => activity.status === "approved"),
  })

  useEffect(() => {
    if (currentActivitiesQuery.status === "success") {
      setCurrentApprovedActivities(currentActivitiesQuery.data || [])
    }
  }, [currentActivitiesQuery.status])

  useEffect(() => {
    if (previousActivitiesQuery.status === "success") {
      setPreviousApprovedActivities(previousActivitiesQuery.data || [])
    }
  }, [previousActivitiesQuery.status])

  const percentage = useMemo(() => {
    if (previousApprovedActivities.length === 0 || currentApprovedActivities.length === 0) return 0

    return (
      ((currentApprovedActivities.length - previousApprovedActivities.length) /
        previousApprovedActivities.length) *
      100
    )
  }, [currentApprovedActivities.length, previousApprovedActivities.length])

  return {
    isPending: currentActivitiesQuery.isPending || previousActivitiesQuery.isPending,
    approvedActivities: currentApprovedActivities.length,
    percentage,
  }
}
